import { FC, useState, useEffect, memo } from 'react';
import { useTranslation, Search, MLPVersion, TextLink, TextLinkStyle } from '@nextgen-web-framework/all';
import { Logger } from '@nextgen-web-framework/core';
import {
  DynamicObjectInterface,
  ContactType,
  profileApi,
  RegionalAddressSearchTypeEnum,
  StateData,
} from '@commerce-profile-webcomponents/sdk';
import logObject from '../../../../logObject';
import { parseString } from '../../../utils/helper';
import { useLocale } from '../../../utils/customHooks';
import { useGlobalErrorHandler } from '../../../utils/globalErrorHook';
import { MyAccountInput, MyAccountOption } from '../../../utils/types/propTypes';
import { useCountryMasterData, useMyAccountAddressConfig } from '../../../utils/masterdata';
import { addressFieldLabel, getAddressConfig } from '../../../utils/myAccountAddressUtils';
import { getExt } from '../../../utils/myAccountProfileUtils';
import { MyAccountExtEnum } from '../../../utils/types/enum';
import { ADDRESS_FIELDS } from '../../../utils/Constants';
import { AreaFilterProps } from './types/propTypes';
import styles from './AreaFilter.module.scss';

const AreaFilter: FC<AreaFilterProps> = ({ contactType = ContactType.Shipping, className, callback, state, city }) => {
  const t = useTranslation();

  const apiErrorHandler = useGlobalErrorHandler();
  const logger = Logger.getInstance(logObject('AreaFilter.tsx', 'AreaFilter'));

  const currentLocale = useLocale();
  const [shortLocaleCode] = String(currentLocale).split('-');

  const [firstFieldLabel, setFirstFieldLabel] = useState<string>('');
  const [secondFieldLabel, setSecondFieldLabel] = useState<string>('');
  const [workingField, setWorkingField] = useState<string>(ADDRESS_FIELDS.STATE);

  const defaultValue = {
    [ADDRESS_FIELDS.STATE]: '',
    [ADDRESS_FIELDS.DISTRICT]: '',
  };
  const defaultOptionData = {
    [ADDRESS_FIELDS.STATE]: [],
    [ADDRESS_FIELDS.DISTRICT]: [],
  };
  const [searchKeys, setSearchKeys] = useState<string>('');
  const [selectedData, setSelectedData] = useState<DynamicObjectInterface>(defaultValue);
  const [optionData, setOptionData] = useState<DynamicObjectInterface>(defaultOptionData);

  const [isLoading, setIsLoading] = useState(false);

  const { currentCountry } = useCountryMasterData();
  const countryCode = currentCountry?.pk?.toUpperCase();

  const addrConfigMaster = useMyAccountAddressConfig();
  const { inputs } = getAddressConfig(addrConfigMaster, contactType);

  useEffect(() => {
    fetchStateData();
  }, []);

  useEffect(() => {
    if (inputs?.length > 0) {
      prepareMetaData();
    }
  }, [inputs]);

  useEffect(() => {
    if (state) {
      setSelectedData((prevState: DynamicObjectInterface) => ({
        ...prevState,
        [ADDRESS_FIELDS.STATE]: state,
        // Add selected city, if any
        ...(city ? { [ADDRESS_FIELDS.DISTRICT]: city } : {}),
      }));
      // Set working field to district if city is provided
      setWorkingField(ADDRESS_FIELDS.DISTRICT);
      // Fetch city data based on the provided state
      fetchCityData(state);
    }
  }, [state, city]);

  const prepareMetaData = () => {
    if (inputs?.length > 0) {
      const firstInput = inputs.find((input: MyAccountInput) => input.fieldName === ADDRESS_FIELDS.STATE);
      const firstFieldCustomLabel = getExt(MyAccountExtEnum.CUSTOM_LABEL_KEY, firstInput?.ext) as string;
      setFirstFieldLabel(
        addressFieldLabel(t, parseString(firstInput?.fieldName), contactType, false, firstFieldCustomLabel),
      );

      const secondInput = inputs.find(
        (input: MyAccountInput) =>
          input.fieldName === ADDRESS_FIELDS.DISTRICT ||
          input?.ext?.find(
            (ext: MyAccountOption) => ext.key === MyAccountExtEnum.SEARCH_KEY && ext.value === ADDRESS_FIELDS.DISTRICT,
          ),
      );
      const secondFieldCustomLabel = getExt(MyAccountExtEnum.CUSTOM_LABEL_KEY, secondInput?.ext) as string;

      setSecondFieldLabel(
        addressFieldLabel(t, parseString(secondInput?.fieldName), contactType, false, secondFieldCustomLabel),
      );
    }
  };

  const fetchStateData = async () => {
    setIsLoading(true);
    const data = await apiErrorHandler(() =>
      profileApi.getAddressRegionData(RegionalAddressSearchTypeEnum.STATE, shortLocaleCode?.toLowerCase()),
    );

    const optionData = data
      .map((item: StateData) => {
        const displayName = t(`profile.AddressInput.state.${countryCode}.${item?.state?.replaceAll(' ', '_')}`);
        return {
          value: parseString(item?.state),
          name: parseString(displayName),
        };
      })
      ?.sort((a: DynamicObjectInterface, b: DynamicObjectInterface) => a.name.localeCompare(b.name));

    setOptionData((prevState: DynamicObjectInterface) => ({
      ...prevState,
      [ADDRESS_FIELDS.STATE]: optionData,
    }));
    setIsLoading(false);
  };

  const fetchCityData = async (stateValue: string) => {
    try {
      setIsLoading(true);
      const data = await apiErrorHandler(() =>
        profileApi.getAddressRegionSearch(RegionalAddressSearchTypeEnum.DISTRICT, {
          state: stateValue,
          language: shortLocaleCode?.toLowerCase(),
        }),
      );
      const optionData = data
        .map((item: DynamicObjectInterface) => ({
          value: parseString(item),
          name: parseString(item),
        }))
        ?.sort((a: DynamicObjectInterface, b: DynamicObjectInterface) => a.name.localeCompare(b.name));
      setOptionData((prevState: DynamicObjectInterface) => ({
        ...prevState,
        [ADDRESS_FIELDS.DISTRICT]: optionData,
      }));

      setIsLoading(false);
    } catch (e) {
      logger.error('Error fetching guided data', e);
    }
  };

  const handleChangeState = () => {
    setWorkingField(ADDRESS_FIELDS.STATE);
    setSelectedData(defaultValue);
    setSearchKeys('');
  };

  const handleChangeDistrict = () => {
    setWorkingField(ADDRESS_FIELDS.DISTRICT);
    setSelectedData({
      ...selectedData,
      [ADDRESS_FIELDS.DISTRICT]: '',
    });
    setSearchKeys('');
  };

  const handleFieldChange = async (fieldName: string, value: string) => {
    setSearchKeys('');
    setSelectedData((prevState: DynamicObjectInterface) => ({
      ...prevState,
      [fieldName]: value,
    }));

    if (fieldName === ADDRESS_FIELDS.STATE) {
      setWorkingField(ADDRESS_FIELDS.DISTRICT);
      setOptionData((prevState: DynamicObjectInterface) => ({
        ...prevState,
        [ADDRESS_FIELDS.DISTRICT]: [],
      }));
      await fetchCityData(value);
    }

    if (fieldName === ADDRESS_FIELDS.DISTRICT) {
      callback?.({
        [ADDRESS_FIELDS.STATE]: selectedData[ADDRESS_FIELDS.STATE],
        [ADDRESS_FIELDS.DISTRICT]: value,
        city: value,
      });
    }
  };

  const handleSearchKeywordChange = (fieldName: string, value: string) => {
    setSearchKeys(value);
  };

  const handleClearSearchKeyword = () => {
    setSearchKeys('');
  };

  const optionResults = optionData[workingField]?.filter(
    (item: DynamicObjectInterface) =>
      item?.name?.toLocaleLowerCase()?.includes(searchKeys?.toLocaleLowerCase()) === true,
  );

  return (
    <div className={`${styles['area-filter-container']} ${className}`} data-testid="area-filter-component">
      <div className={styles['area-filter-header']}>
        <div className={styles['search-label-container']}>
          <div className={styles['search-label-state']}>{firstFieldLabel}</div>
          {selectedData[ADDRESS_FIELDS.STATE] && (
            <TextLink
              onClick={handleChangeState}
              text={t('profile.common.change')}
              textLinkStyle={TextLinkStyle.UNDERLINE}
              data-testid="change-link"
            />
          )}
        </div>
        {!selectedData[ADDRESS_FIELDS.STATE] && (
          <Search
            mlpVersion={MLPVersion.TWO}
            className={styles['searchInputBox']}
            onChange={(e) => handleSearchKeywordChange(ADDRESS_FIELDS.STATE, e.target.value)}
            value={searchKeys}
            onSearch={() => null}
            onClear={handleClearSearchKeyword}
            data-testId="search-input-state"
          />
        )}
        {selectedData[ADDRESS_FIELDS.STATE] && (
          <>
            <div className={styles['selected-item']}>
              {
                optionData[ADDRESS_FIELDS.STATE]?.find(
                  (item: DynamicObjectInterface) => item?.value === selectedData[ADDRESS_FIELDS.STATE],
                )?.name
              }
            </div>

            <div className={styles['search-label-container']}>
              <div className={styles['search-label-district']}>{secondFieldLabel}</div>
              {selectedData[ADDRESS_FIELDS.DISTRICT] && (
                <TextLink
                  onClick={handleChangeDistrict}
                  text={t('profile.common.change')}
                  textLinkStyle={TextLinkStyle.UNDERLINE}
                  data-testid="change-link"
                />
              )}
            </div>

            {selectedData[ADDRESS_FIELDS.DISTRICT] ? (
              <div className={`${styles['selected-item']} ${styles['district']}`} data-testid="selected-district">
                {
                  optionData[ADDRESS_FIELDS.DISTRICT]?.find(
                    (item: DynamicObjectInterface) => item?.value === selectedData[ADDRESS_FIELDS.DISTRICT],
                  )?.name
                }
              </div>
            ) : (
              <Search
                mlpVersion={MLPVersion.TWO}
                className={styles['searchInputBox']}
                onChange={(e) => handleSearchKeywordChange(ADDRESS_FIELDS.DISTRICT, e.target.value)}
                value={searchKeys}
                onSearch={() => null}
                onClear={handleClearSearchKeyword}
                data-testId="search-input-district"
              />
            )}
          </>
        )}
      </div>

      <div className={styles['search-results']}>
        {!selectedData[workingField] &&
          optionResults?.map((item: DynamicObjectInterface) => (
            <div
              onClick={() => handleFieldChange(workingField, item?.value)}
              tabIndex={-1}
              aria-hidden="true"
              data-testId="search-list"
              className={`${styles['search-item']} ${
                selectedData[workingField] === item?.value ? styles['selected'] : ''
              }`}
              key={item.code}
            >
              {item?.name}
            </div>
          ))}
        {optionResults?.length === 0 && !isLoading && <>{t('profile.common.noRecord')}</>}
        {isLoading && <>{t('web.loader.title')}</>}
      </div>
    </div>
  );
};

export default memo(AreaFilter);
